/**
 * ReportParticipants — "Osallistujat" section.
 *
 * Data resolution (in order):
 *  1. Curated list from data/kuutio_{N}/participants.md
 *     (full name + title + organisation, maintained by coordinator).
 *     Endpoint: API_ENDPOINTS.INSTANCE_CURATED_PARTICIPANTS(instanceId)
 *  2. Fallback: auth_pins PIN registrations (just display_name).
 *  3. Manual admin EditableText (ExtrasBox) — for ad-hoc additions.
 *
 * Käsikirjoitus 2026-05: list format = nimi · titteli · organisaatio.
 * The curated markdown file is the source of truth — the auth_pins list
 * usually covers only a handful of PIN-registered devices.
 */
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Users, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { Section, SectionTitle, Paragraph, SITRA } from './reportStyles';
import EditableText from './EditableText';
import GroupPhoto from './GroupPhoto';
import { API_ENDPOINTS } from '../../config/api';
import { useReportEdits } from './ReportEditsContext';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const Header = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${SITRA.blue};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 16px 0 12px;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  background: ${SITRA.white};
  border: 1px solid ${SITRA.border};
`;

const Row = styled.li`
  display: grid;
  grid-template-columns: minmax(180px, 1fr) 1.4fr 1.4fr;
  gap: 16px;
  padding: 12px 20px;
  border-bottom: 1px solid ${SITRA.border};
  font-size: 0.95rem;
  align-items: center;
  &:last-child { border-bottom: none; }
  @media (max-width: 680px) {
    grid-template-columns: 1fr;
    gap: 2px;
    padding: 14px 16px;
  }
`;

const SubHeading = styled.h3`
  margin: 24px 0 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${SITRA.textBody};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  display: flex;
  align-items: baseline;
  gap: 10px;
  &:first-of-type { margin-top: 12px; }
`;

const SubHeadingCount = styled.span`
  font-size: 0.78rem;
  font-weight: 500;
  color: ${SITRA.textMuted};
  text-transform: none;
  letter-spacing: 0;
`;

const Name = styled.div`
  font-weight: 600;
  color: ${SITRA.black};
`;

const Cell = styled.div`
  color: ${SITRA.textBody};
  font-size: 0.92rem;
  @media (max-width: 680px) {
    font-size: 0.85rem;
    color: ${SITRA.textMuted};
  }
`;

const SourceTag = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: ${p => p.$source === 'curated' ? '#ecfdf5' : '#fff7ed'};
  color: ${p => p.$source === 'curated' ? SITRA.havu : '#9a3412'};
`;

const ExtrasBox = styled.div`
  background: ${SITRA.surfaceMuted};
  padding: 20px 24px;
  border-left: 4px solid ${SITRA.black};
  margin-top: 24px;
  font-size: 0.95rem;
  line-height: 1.7;
  color: ${SITRA.textBody};
  white-space: pre-line;
`;

const Loading = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px;
  color: ${SITRA.textMuted};
`;

const HintBox = styled.div`
  padding: 16px 20px;
  margin-top: 12px;
  color: ${SITRA.textMuted};
  background: ${SITRA.surfaceMuted};
  font-size: 0.85rem;
  font-style: italic;
`;

export default function ReportParticipants({ instanceId, count }) {
  const { t } = useTranslation();
  const { canEdit, editMode } = useReportEdits();
  const [data, setData] = useState({ list: [], source: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!instanceId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);

    // Try curated list first; fall back to auth_pins if file missing.
    (async () => {
      try {
        const curatedRes = await fetch(API_ENDPOINTS.INSTANCE_CURATED_PARTICIPANTS(instanceId));
        if (curatedRes.ok) {
          const curatedJson = await curatedRes.json();
          if (Array.isArray(curatedJson?.participants) && curatedJson.participants.length > 0) {
            if (!cancelled) {
              setData({ list: curatedJson.participants, source: 'curated' });
              setError(null);
              setLoading(false);
            }
            return;
          }
        }

        // No auth_pins fallback. Pins are access codes, not people: across
        // every instance in prod their display names are workshop titles
        // ("Kuutio 7: Kela"), organisations ("Sisäministeriö") or device
        // labels ("Tuparit-screen"). Rendering them as participants gave K7 a
        // list of one person named after the workshop, and — because
        // headerCount prefers the fetched list over the real count — a header
        // reading "1 osallistujat" for a room of 36.
        //
        // With no curated list we show none, and headerCount falls through to
        // the instance's own participant_count.
        if (!cancelled) {
          setData({ list: [], source: 'empty' });
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [instanceId]);

  const showExtras = (canEdit && editMode) || error;
  const { list, source } = data;

  // Split by role for sub-section rendering. Entries without an explicit
  // role (e.g. the auth_pins fallback path) fall into 'participant' so older
  // data shapes keep working unchanged.
  const participantRows  = list.filter(p => !p.role || p.role === 'participant');
  const commissionerRows = list.filter(p => p.role === 'commissioner');
  const teamRows         = list.filter(p => p.role === 'team');
  const headerCount = participantRows.length || list.length || count;

  // Render a labelled subsection only when it has rows. Keeps the report
  // tidy if a file is missing the Toimeksiantaja or Kuutio-tiimiläiset
  // section (older participants.md files don't have them).
  const renderRows = (rows, keyPrefix) => (
    <List>
      {rows.map((p, i) => (
        <Row key={p._pin || `${keyPrefix}-${p.name}-${i}`}>
          <Name>{p.name}</Name>
          <Cell>
            {p.title || (canEdit && editMode
              ? <EditableText
                  fieldKey={`participant.${p._pin || keyPrefix + i}.title`}
                  fallback="—"
                  label={`${p.name} — titteli`}
                  multiline={false}
                />
              : '')}
          </Cell>
          <Cell>
            {p.organisation || (canEdit && editMode
              ? <EditableText
                  fieldKey={`participant.${p._pin || keyPrefix + i}.organisation`}
                  fallback="—"
                  label={`${p.name} — organisaatio`}
                  multiline={false}
                />
              : '')}
          </Cell>
        </Row>
      ))}
    </List>
  );

  return (
    <Section id="participants">
      <SectionTitle>{t('report.nav.participants')}</SectionTitle>
      <GroupPhoto />
      <Paragraph>
        {t('report.participants.intro')}
      </Paragraph>

      <Header
        onClick={() => list.length > 0 && setExpanded(e => !e)}
        style={list.length > 0 ? { cursor: 'pointer', userSelect: 'none' } : undefined}
      >
        <Users size={16} />
        {headerCount != null ? `${headerCount} ${t('report.participants.header')}` : t('report.participants.header')}
        {list.length > 0 && (
          expanded
            ? <ChevronUp size={16} style={{ marginLeft: 4 }} />
            : <ChevronDown size={16} style={{ marginLeft: 4 }} />
        )}
        {canEdit && editMode && source && (
          <SourceTag $source={source}>
            {source === 'curated' ? t('report.participants.curatedSource') : '—'}
          </SourceTag>
        )}
      </Header>

      {loading && (
        <Loading>
          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
          {t('report.participants.loading')}
        </Loading>
      )}

      {!loading && list.length > 0 && expanded && (
        <>
          {participantRows.length > 0 && renderRows(participantRows, 'p')}
          {commissionerRows.length > 0 && (
            <>
              <SubHeading>
                {t('report.participants.commissioner')}
                <SubHeadingCount>({commissionerRows.length})</SubHeadingCount>
              </SubHeading>
              {renderRows(commissionerRows, 'c')}
            </>
          )}
          {teamRows.length > 0 && (
            <>
              <SubHeading>
                {t('report.participants.team')}
                <SubHeadingCount>({teamRows.length})</SubHeadingCount>
              </SubHeading>
              {renderRows(teamRows, 't')}
            </>
          )}
        </>
      )}

      {!loading && list.length === 0 && (
        <HintBox>
          {t('report.participants.noListHint')}
        </HintBox>
      )}

      {showExtras && (
        <ExtrasBox>
          <strong style={{ display: 'block', marginBottom: 8, color: SITRA.textBody }}>
            {t('report.participants.extras')}
          </strong>
          <EditableText
            fieldKey="participants.list"
            fallback={t('report.participants.extras')}
            label="Osallistujien lisätiedot"
          />
        </ExtrasBox>
      )}
    </Section>
  );
}
